import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const ReviewSection = ({ title, onEdit, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="flex items-center justify-between gap-3">
      <h4 className="font-semibold text-slate-900">{title}</h4>
      <Button type="button" variant="ghost" size="sm" onClick={onEdit} className="text-[#16877c] hover:text-[#116d64]">
        Edit <span className="sr-only">{title}</span>
      </Button>
    </div>
    <div className="mt-3 text-sm text-slate-600">{children}</div>
  </section>
);

export const ProgramLaunchChecklist = ({
  programDetails,
  divisions,
  paymentPlans,
  documents,
  addons,
  emailSettings,
  stepFor,
  onEdit,
}) => {
  const publicPath = programDetails.slug ? `/r/${programDetails.slug}` : "Created after publishing";

  return (
    <div className="space-y-4" aria-label="Launch checklist">
      <ReviewSection title="Public registration" onEdit={() => onEdit(stepFor.basics)}>
        <dl className="grid gap-3 sm:grid-cols-2">
          <div><dt className="font-medium text-slate-800">Public page</dt><dd>{publicPath}</dd></div>
          <div><dt className="font-medium text-slate-800">Program dates</dt><dd>{programDetails.start_date || "Not set"} – {programDetails.end_date || "Not set"}</dd></div>
          <div><dt className="font-medium text-slate-800">Registration opens</dt><dd>{programDetails.registration_open_date || "Immediately on publish"}</dd></div>
          <div><dt className="font-medium text-slate-800">Registration closes</dt><dd>{programDetails.registration_deadline || "Not set"}</dd></div>
        </dl>
      </ReviewSection>

      <ReviewSection title="Divisions, pricing, and capacity" onEdit={() => onEdit(stepFor.divisions)}>
        <ul className="divide-y divide-slate-100">
          {divisions.map((division, index) => (
            <li key={division.division_id || `${division.name}-${index}`} className="flex flex-wrap justify-between gap-2 py-2 first:pt-0 last:pb-0">
              <span className="font-medium text-slate-800">{division.name || `Division ${index + 1}`}</span>
              <span>{money(division.price)} · {Number(division.max_players || 0)} spots</span>
            </li>
          ))}
        </ul>
      </ReviewSection>

      <ReviewSection title="Payment options" onEdit={() => onEdit(stepFor.payments)}>
        {paymentPlans.length ? (
          <ul className="space-y-2">
            {paymentPlans.map((plan, index) => (
              <li key={plan.plan_id || `${plan.name}-${index}`}>
                <span className="font-medium text-slate-800">{plan.name || `Plan ${index + 1}`}</span>
                {` · ${Number(plan.num_payments || 1)} payment${Number(plan.num_payments || 1) === 1 ? "" : "s"} · ${money(plan.deposit)} deposit`}
              </li>
            ))}
          </ul>
        ) : <p>Full payment at registration.</p>}
      </ReviewSection>

      <ReviewSection title="Forms and waivers" onEdit={() => onEdit(stepFor.documents)}>
        <p><span className="font-medium text-slate-800">Required documents:</span> {documents.length ? documents.map((document) => document.name || document.title).filter(Boolean).join(", ") || `${documents.length} configured` : "None"}</p>
      </ReviewSection>

      <ReviewSection title="Registration add-ons" onEdit={() => onEdit(stepFor.addons)}>
        <p><span className="font-medium text-slate-800">Add-ons:</span> {addons.length ? addons.map((addon) => addon.name).filter(Boolean).join(", ") || `${addons.length} configured` : "None"}</p>
      </ReviewSection>

      <ReviewSection title="Confirmation email" onEdit={() => onEdit(stepFor.email)}>
        <p className="font-medium text-slate-800">{emailSettings.confirmation_subject || "Registration confirmation"}</p>
        <p className="mt-2 line-clamp-3 whitespace-pre-wrap">{emailSettings.confirmation_body || "No confirmation message configured."}</p>
      </ReviewSection>
    </div>
  );
};

export const ProgramPublishConfirmation = ({ open, onOpenChange, programDetails, divisions, saving, onConfirm }) => {
  const capacity = divisions.reduce((total, division) => total + (Number(division.max_players) || 0), 0);
  const prices = divisions.map((division) => Number(division.price)).filter(Number.isFinite);
  const priceRange = prices.length
    ? `${money(Math.min(...prices))}${Math.min(...prices) === Math.max(...prices) ? "" : `–${money(Math.max(...prices))}`}`
    : money(0);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Publish {programDetails.name || "this program"}?</AlertDialogTitle>
          <AlertDialogDescription>
            This creates the public registration page. Registration will {programDetails.registration_open_date ? `open on ${programDetails.registration_open_date}` : "open immediately"}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <dl className="grid gap-3 rounded-xl bg-slate-50 p-4 text-sm sm:grid-cols-2">
          <div><dt className="font-medium text-slate-800">Capacity</dt><dd className="text-slate-600">{capacity} spots</dd></div>
          <div><dt className="font-medium text-slate-800">Price range</dt><dd className="text-slate-600">{priceRange}</dd></div>
          <div className="sm:col-span-2"><dt className="font-medium text-slate-800">Registration closes</dt><dd className="text-slate-600">{programDetails.registration_deadline || "No closing date"}</dd></div>
        </dl>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving}>Keep reviewing</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={saving} className="bg-[#16877c] text-white hover:bg-[#116d64]">
            {saving ? "Publishing…" : "Publish program"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
